/**
 * Casting Service
 *
 * Provides casting/mirroring functionality for media to Smart TVs
 * Supports:
 * - Google Cast (Chromecast)
 * - AirPlay (Safari on macOS/iOS)
 * - DLNA (via third-party solutions)
 */

/* global chrome, cast */

class CastService {
  constructor() {
    this.castContext = null;
    this.castPlayer = null;
    this.isInitialized = false;
    this.availableDevices = [];
    this.currentSession = null;
    this.isCasting = false;
  }

  /**
   * Initialize Google Cast SDK
   */
  async initializeCast() {
    if (this.isInitialized) {
      return true;
    }

    // Check if Cast API is available
    if (!window.chrome?.cast || !window.chrome?.cast?.isAvailable) {
      console.warn('Google Cast API not available');
      return false;
    }

    try {
      // Load Cast SDK script if not already loaded
      if (!window.cast) {
        await this.loadCastSDK();
      }

      // Initialize Cast context
      const castApplicationId = 'CC1AD845'; // Default Media Receiver App ID
      this.castContext = cast.framework.CastContext.getInstance();
      this.castContext.setOptions({
        receiverApplicationId: castApplicationId,
        autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
      });

      this.isInitialized = true;
      console.log('✅ Google Cast initialized');
      return true;
    } catch (error) {
      console.error('Error initializing Cast:', error);
      return false;
    }
  }

  /**
   * Load Google Cast SDK script
   */
  loadCastSDK() {
    return new Promise((resolve, reject) => {
      // If already loaded, resolve immediately
      if (window.cast && window.cast.framework) {
        resolve();
        return;
      }

      // If script is already being loaded, wait for it
      if (document.querySelector('script[src*="cast_sender.js"]')) {
        // Wait for Cast API to become available
        const checkInterval = setInterval(() => {
          if (window.cast && window.cast.framework) {
            clearInterval(checkInterval);
            resolve();
          }
        }, 100);

        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(checkInterval);
          if (window.cast && window.cast.framework) {
            resolve();
          } else {
            reject(new Error('Cast SDK loading timeout'));
          }
        }, 10000);
        return;
      }

      // Set up callback before loading script
      window['__onGCastApiAvailable'] = (isAvailable) => {
        if (isAvailable) {
          resolve();
        } else {
          reject(new Error('Cast API not available'));
        }
      };

      const script = document.createElement('script');
      script.src =
        'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1';
      script.async = true;

      script.onload = () => {
        // Script loaded, but callback might be called asynchronously
        // Wait a bit for the callback
        setTimeout(() => {
          if (window.cast && window.cast.framework) {
            resolve();
          } else {
            // If callback wasn't called, check again after a delay
            setTimeout(() => {
              if (window.cast && window.cast.framework) {
                resolve();
              } else {
                reject(new Error('Cast API not available after script load'));
              }
            }, 2000);
          }
        }, 500);
      };

      script.onerror = () => {
        delete window['__onGCastApiAvailable'];
        reject(new Error('Failed to load Cast SDK script'));
      };

      try {
        document.head.appendChild(script);
      } catch (error) {
        delete window['__onGCastApiAvailable'];
        reject(error);
      }
    });
  }

  /**
   * Check if AirPlay is available (Safari only)
   */
  isAirPlayAvailable() {
    const isSafari =
      /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
      /iPad|iPhone|iPod/.test(navigator.userAgent);
    return isSafari;
  }

  /**
   * Check if Google Cast is available
   */
  isCastAvailable() {
    return (
      window.chrome?.cast?.isAvailable || (window.cast && window.cast.framework)
    );
  }

  /**
   * Get available casting devices
   */
  async getAvailableDevices() {
    const devices = [];

    // Check for Chromecast devices
    if (this.isCastAvailable() && this.castContext) {
      try {
        const sessionState = this.castContext.getSessionState();
        if (sessionState === cast.framework.SessionState.SESSION_STARTED) {
          const session = this.castContext.getCurrentSession();
          if (session) {
            devices.push({
              id: session.getSessionId(),
              name: 'Chromecast',
              type: 'chromecast',
              connected: true,
            });
          }
        }
      } catch (error) {
        console.error('Error getting Cast devices:', error);
      }
    }

    // AirPlay is handled natively by Safari's video element
    if (this.isAirPlayAvailable()) {
      devices.push({
        id: 'airplay',
        name: 'AirPlay',
        type: 'airplay',
        available: true,
      });
    }

    this.availableDevices = devices;
    return devices;
  }

  /**
   * Cast media to Chromecast
   */
  async castToChromecast(mediaUrl, metadata = {}) {
    if (!this.isInitialized) {
      const initialized = await this.initializeCast();
      if (!initialized) {
        throw new Error('Cast not available');
      }
    }

    try {
      const mediaInfo = new chrome.cast.media.MediaInfo(mediaUrl, 'video/mp4');
      mediaInfo.metadata = new chrome.cast.media.GenericMediaMetadata();
      mediaInfo.metadata.title = metadata.title || 'Jookbox Media';
      mediaInfo.metadata.subtitle = metadata.artist || '';
      if (metadata.thumbnail) {
        mediaInfo.metadata.images = [new chrome.cast.Image(metadata.thumbnail)];
      }

      const request = new chrome.cast.media.LoadRequest(mediaInfo);
      const session = await this.castContext.requestSession();

      await session.loadMedia(request);
      this.currentSession = session;
      this.isCasting = true;

      console.log('✅ Casting to Chromecast:', mediaUrl);
      return session;
    } catch (error) {
      console.error('Error casting to Chromecast:', error);
      throw error;
    }
  }

  /**
   * Cast audio to Chromecast
   */
  async castAudioToChromecast(audioUrl, metadata = {}) {
    if (!this.isInitialized) {
      const initialized = await this.initializeCast();
      if (!initialized) {
        throw new Error('Cast not available');
      }
    }

    try {
      const mediaInfo = new chrome.cast.media.MediaInfo(audioUrl, 'audio/mp3');
      mediaInfo.metadata = new chrome.cast.media.MusicTrackMediaMetadata();
      mediaInfo.metadata.title = metadata.title || 'Jookbox Music';
      mediaInfo.metadata.artist = metadata.artist || '';
      if (metadata.thumbnail) {
        mediaInfo.metadata.images = [new chrome.cast.Image(metadata.thumbnail)];
      }

      const request = new chrome.cast.media.LoadRequest(mediaInfo);
      const session = await this.castContext.requestSession();

      await session.loadMedia(request);
      this.currentSession = session;
      this.isCasting = true;

      console.log('✅ Casting audio to Chromecast:', audioUrl);
      return session;
    } catch (error) {
      console.error('Error casting audio to Chromecast:', error);
      throw error;
    }
  }

  /**
   * Stop casting
   */
  async stopCasting() {
    if (this.currentSession) {
      try {
        await this.currentSession.stop();
        this.currentSession = null;
        this.isCasting = false;
        console.log('✅ Casting stopped');
      } catch (error) {
        console.error('Error stopping cast:', error);
      }
    }
  }

  /**
   * Pause cast playback
   */
  async pauseCast() {
    if (this.currentSession) {
      const media = this.currentSession.getMediaSession();
      if (media) {
        await media.pause();
      }
    }
  }

  /**
   * Resume cast playback
   */
  async resumeCast() {
    if (this.currentSession) {
      const media = this.currentSession.getMediaSession();
      if (media) {
        await media.play();
      }
    }
  }

  /**
   * Seek in cast playback
   */
  async seekCast(currentTime) {
    if (this.currentSession) {
      const media = this.currentSession.getMediaSession();
      if (media) {
        const seekRequest = new chrome.cast.media.SeekRequest();
        seekRequest.currentTime = currentTime;
        await media.seek(seekRequest);
      }
    }
  }

  /**
   * Set volume for cast playback
   */
  async setCastVolume(volume) {
    if (this.currentSession) {
      const media = this.currentSession.getMediaSession();
      if (media) {
        const volume = new chrome.cast.Volume();
        volume.level = Math.max(0, Math.min(1, volume));
        await this.currentSession.setReceiverVolumeLevel(volume.level);
      }
    }
  }

  /**
   * Get current cast status
   */
  getCastStatus() {
    if (!this.currentSession) {
      return {
        isCasting: false,
        device: null,
        media: null,
      };
    }

    const media = this.currentSession.getMediaSession();
    return {
      isCasting: true,
      device: {
        name: 'Chromecast',
        type: 'chromecast',
      },
      media: media
        ? {
            title: media.media?.metadata?.title,
            currentTime: media.getEstimatedTime(),
            duration: media.media?.duration,
            playerState: media.playerState,
          }
        : null,
    };
  }

  /**
   * Listen for cast session changes
   */
  onSessionChange(callback) {
    if (this.castContext) {
      this.castContext.addEventListener(
        cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
        (event) => {
          this.isCasting =
            event.sessionState === cast.framework.SessionState.SESSION_STARTED;
          this.currentSession = this.castContext.getCurrentSession();
          callback(event);
        }
      );
    }
  }
}

// Export singleton instance
export const castService = new CastService();
export default castService;
